import React, { useState, useEffect } from "react";
import { Form, Button, Container, Card, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../config/Supabase";
import { useAuth } from "../../context/AuthContext";
import "../../styles/General.css";
import "../../styles/Login.css";
import { trackLogin } from "../../utils/analytics";

// Pick a landing route based on the user's detected roles.
const landingForRoles = (roles) => {
  if (roles?.isAdmin) return "/maral1362";
  if (roles?.isAffiliate) return "/affiliate-dashboard";
  if (roles?.isPremium) return "/explorer";
  return "/";
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [oauthInFlight, setOauthInFlight] = useState(false);
  const navigate = useNavigate();
  const { user, roles, loading: authLoading, profileReady, refresh } = useAuth();

  useEffect(() => {
    // If the user just signed out, clean any leftover OAuth hash and bail.
    const justSignedOut = (() => {
      try { return sessionStorage.getItem("just_signed_out"); } catch { return null; }
    })();
    if (justSignedOut) {
      try { sessionStorage.removeItem("just_signed_out"); } catch {}
      if (window.location.hash) {
        window.history.replaceState(null, "", window.location.pathname);
      }
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith("sb-") || k.includes("supabase"))
          .forEach((k) => localStorage.removeItem(k));
      } catch {}
      supabase.auth.signOut({ scope: "local" }).catch(() => {});
    }
  }, []);

  // Destructure to primitives so the effect only re-runs when actual values
  // change, not when the roles object reference changes on every context render.
  const { isAdmin, isAffiliate, isPremium } = roles;

  // Wait until the profile has fully loaded before routing, so roles are
  // resolved and the user lands on the right page (not "/" due to empty roles).
  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    if (!profileReady) return;
    const dest = landingForRoles({ isAdmin, isAffiliate, isPremium });
    trackLogin(user.app_metadata?.provider || "email");
    navigate(dest, { replace: true });
  }, [authLoading, user, profileReady, isAdmin, isAffiliate, isPremium, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) throw signInError;
      // Force context to refresh immediately so the redirect effect fires
      // without waiting for the auth-change listener.
      await refresh();
    } catch (err) {
      console.error("[login] sign-in failed:", err);
      setError(err.message || "Failed to sign in. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const signInWithOAuth = async (provider) => {
    setError("");
    setOauthInFlight(true);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/login` },
    });
    if (oauthError) {
      setOauthInFlight(false);
      setError(
        `Failed to sign in with ${provider === "google" ? "Google" : "Apple"}.`
      );
    }
  };

  return (
    <Container
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "100vh" }}
    >
      <div className="w-100" style={{ maxWidth: "500px", paddingTop: "60px" }}>
        <Card className="shadow no-border-card" style={{ height: "auto" }}>
          <Card.Body>
            <Card.Title className="login-card-title">
              Login to your account
            </Card.Title>
            <div
              className="w-100"
              style={{ maxWidth: "400px", paddingTop: "25px" }}
            ></div>
            <Form onSubmit={signIn}>
              <Form.Group controlId="formBasicEmail">
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Form.Group>

              <div style={{ paddingTop: "20px", paddingBottom: "25px" }}>
                <Form.Group controlId="formBasicPassword">
                  <Form.Control
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Form.Group>
              </div>

              {error && (
                <Alert
                  variant="danger"
                  onClose={() => setError("")}
                  dismissible
                >
                  {error}
                </Alert>
              )}

              <div style={{ paddingTop: "20px" }}>
                <Button
                  variant="primary"
                  type="submit"
                  className="login-btn w-100 mt-3"
                  disabled={loading || oauthInFlight}
                >
                  {loading ? "Signing in…" : "Sign In"}
                </Button>
              </div>
              <Button
                variant="primary"
                type="button"
                onClick={() => signInWithOAuth("google")}
                disabled={loading || oauthInFlight}
                className="w-100 mt-3 google-sign-up-button"
              >
                Sign In With Google
              </Button>

              <div className="text-center mt-3">
                <a href="/forgotPassword">Forgot Password?</a>
              </div>
              <div className="text-center mt-3">
                <a href="/signUp">Don't Have an Account? Sign Up</a>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default Login;
